import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { imgSize200 } from 'constants/image';
import { useIsMobile } from 'hooks/useMedia';
import { formatImagelink } from 'utils';
import { getTestIdByParts } from 'utils/getTestIdByParts';
import type { WithTestId } from 'types/app';

import type { ImageType } from '@Modules/upload/types';

import { Button } from '@Atomic/atoms/Button';
import { Title } from '@Atomic/atoms/Headline';
import { Icons } from '@Atomic/atoms/Icons';
import { Image } from '@Atomic/atoms/Image';
import { LinkRouter } from '@Atomic/atoms/LinkRouter';

import styles from './categoryRedirectCard.module.scss';

type Props = WithTestId<{
  categoryHref: string;
  categoryName?: string;
  categoryImage?: ImageType | null;
  className?: string;
}>;

export const CategoryRedirectCard: FC<Props> = ({
  categoryHref,
  categoryName,
  categoryImage,
  className,
  testId = 'category-redirect-card',
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const imageSrc = categoryImage ? formatImagelink(categoryImage.cropUrlTemplate, imgSize200) : '';

  const cardBody = (
    <>
      <div className={styles.header}>
        {imageSrc && (
          <div className={styles.imageWrapper}>
            <Image src={imageSrc} alt="" className={styles.image} />
          </div>
        )}
        <div className={styles.text}>
          <div className={styles.titleRow}>
            <Title.H3 className={styles.title} testId={getTestIdByParts(testId, 'title')}>
              {t('offer.closed.categoryCard.title')}
            </Title.H3>
            {isMobile && <Icons.ChevronRight className={styles.titleChevron} aria-hidden="true" />}
          </div>
          <div className={styles.subtitle} data-testid={getTestIdByParts(testId, 'subtitle')}>
            {categoryName
              ? t('offer.closed.categoryCard.subtitle', { categoryName })
              : t('offer.closed.categoryCard.subtitleFallback')}
          </div>
        </div>
      </div>

      {!isMobile && (
        <Button
          to={categoryHref}
          intent="neutral"
          presentation="tonal"
          fullWidth
          className={styles.cta}
          testId={getTestIdByParts(testId, 'cta')}
        >
          {t('offer.closed.categoryCard.cta')}
        </Button>
      )}
    </>
  );

  if (isMobile) {
    return (
      <LinkRouter
        to={categoryHref}
        className={cn(styles.card, styles.cardLink, className)}
        testId={testId}
      >
        {cardBody}
      </LinkRouter>
    );
  }

  return (
    <div className={cn(styles.card, className)} data-testid={testId}>
      {cardBody}
    </div>
  );
};
